/**
 * FO-373 (batch-42) — Leverwijze: "uit voorraad" of "extern bestellen".
 *
 * Gedeelde enum, labels, DTO-bouwstenen en pure helpers voor het ene
 * keuzepatroon (`LeverwijzeKiezer`) dat over alle toeken-/bestelflows loopt:
 * uitrusting-beoordeling, gereedschap-uitgifte-set en directe toewijzing. De
 * karren/koffers-flow houdt zijn eigen, rijkere `DragerBestelActie`; hier
 * trekken we alleen het component gelijk.
 *
 * Spiegelt de Prisma-enum `Leverwijze` (VOORRAAD/EXTERN). Bewust no-deps
 * (alleen `zod`) zodat zowel de API, het `@streep/ui`-component als de portal
 * hetzelfde gebruiken.
 */

import { z } from 'zod'

export const LEVERWIJZE = ['VOORRAAD', 'EXTERN'] as const
export type Leverwijze = (typeof LEVERWIJZE)[number]

/** Korte NL-labels (STREEP-copy; geen em-dash). */
export const LEVERWIJZE_LABELS: Record<Leverwijze, string> = {
  VOORRAAD: 'Uit voorraad',
  EXTERN: 'Extern bestellen',
}

// ─────────────────────────────────────────────────────────────────────────
// batch-45 onboarding (FO-381) — drie mutatie-modi bij toewijzen
// ─────────────────────────────────────────────────────────────────────────

/**
 * De drie mutatie-modi bij het toewijzen van spullen (FO-381 §3.3):
 *  - VOORRAAD      — bestaand pad: reserveren -> uitgeven -> voorraad afgeboekt.
 *  - BESTELLING    — bestelvoorstel met de medewerker als bestemming; uitgifte
 *                    volgt automatisch bij ontvangst (FO-373-mechanisme).
 *  - GEEN_MUTATIE  — alleen het bezit registreren (Middelen-tab + retourplicht),
 *                    zonder voorraadmutatie, met zichtbare markering.
 * Spiegelt de String-kolom `OnboardingAssetReservering.mutatieModus`.
 */
export const MUTATIE_MODUS = ['VOORRAAD', 'BESTELLING', 'GEEN_MUTATIE'] as const
export type MutatieModus = (typeof MUTATIE_MODUS)[number]

export const MUTATIE_MODUS_LABELS: Record<MutatieModus, string> = {
  VOORRAAD: 'Uit voorraad',
  BESTELLING: 'Uit bestelling',
  GEEN_MUTATIE: 'Geen mutaties',
}

/**
 * De niet-onderdrukbare markering op bezit dat zonder voorraadmutatie is
 * geregistreerd (FO-381 §3.3) — één copy-bron voor Middelen-tab, assets-tab en
 * gereedschap-sectie, zodat magazijn overal dezelfde tekst ziet.
 */
export const ZONDER_MUTATIE_MARKERING = 'Geregistreerd zonder voorraadmutatie'

/**
 * Voorraad-/leverancier-context per regel, server-side batch-berekend en
 * meegestuurd in de regel-DTO's (geen client-side voorraad-fetch per regel).
 * Voedt de `LeverwijzeKiezer`: "Uit voorraad - N beschikbaar op {locatie}" /
 * "Extern bestellen - {leverancier}".
 */
export interface RegelVoorraadInfo {
  /** Gekoppelde voorraad-bron (FO-321); null = geen koppeling -> alleen extern. */
  voorraadArtikelId: string | null
  /** Vrije voorraad op de gekoppelde bron (0 als er geen koppeling is). */
  vrij: number
  /** Locatie-aanduiding voor "N beschikbaar op {locatie}" (optioneel). */
  locatieNaam: string | null
  /** Leverancier voor "Extern bestellen ({leverancier})" (optioneel). */
  leverancierNaam: string | null
  /**
   * Totale voorraad op de bron, inclusief wat al gereserveerd is (`vrij` is het
   * deel dat nog te vergeven valt). Het voorraadbeleid hieronder gaat over dit
   * totaal, dus de verwerker moet beide zien om te kunnen beoordelen of hij het
   * magazijn leegtrekt. Null = geen voorraadkoppeling.
   */
  totaalSaldo: number | null
  /** Ondergrens van de bron; 0 of null = geen beleid ingesteld. */
  minimumVoorraad: number | null
  /** Streefvoorraad van de bron; null = niet ingesteld. */
  maximumVoorraad: number | null
}

/** Lege voorraad-context (geen koppeling) - herbruikbare default. */
export const GEEN_VOORRAAD_INFO: RegelVoorraadInfo = {
  voorraadArtikelId: null,
  vrij: 0,
  locatieNaam: null,
  leverancierNaam: null,
  totaalSaldo: null,
  minimumVoorraad: null,
  maximumVoorraad: null,
}

/**
 * Pure default-splitsing: zoveel mogelijk uit voorraad zodra er een koppeling
 * én vrije voorraad is, anders 0 (volledig extern). Zowel het component als de
 * server gebruiken dit zodat de default overal identiek is (A3).
 */
export function defaultAantalVoorraad(
  aantal: number,
  info: { voorraadArtikelId: string | null; vrij: number },
): number {
  if (!info.voorraadArtikelId || info.vrij <= 0) return 0
  return Math.max(0, Math.min(aantal, info.vrij))
}

/** Coarse leverwijze-samenvatting van een splitsing (VOORRAAD zodra er iets uit voorraad komt). */
export function leverwijzeVanSplitsing(aantalVoorraad: number): Leverwijze {
  return aantalVoorraad > 0 ? 'VOORRAAD' : 'EXTERN'
}

/** Telling van uit voorraad geleverde vs extern bestelde stuks over een set regels. */
export interface LeverstatusTelling {
  uitVoorraad: number
  extern: number
}

/**
 * Tel over de regels hoeveel stuks uit voorraad geleverd zijn (`aantalVoorraad`)
 * versus extern besteld (`aantal - aantalVoorraad`). Puur, zodat de monteur-portal
 * én de backoffice dezelfde leverstatus tonen. Klemt defensief op [0, aantal].
 */
export function telLeverstatus(
  regels: Array<{ aantal: number; aantalVoorraad?: number | null }>,
): LeverstatusTelling {
  let uitVoorraad = 0
  let extern = 0
  for (const r of regels) {
    const v = Math.max(0, Math.min(r.aantalVoorraad ?? 0, r.aantal))
    uitVoorraad += v
    extern += r.aantal - v
  }
  return { uitVoorraad, extern }
}

// ─────────────────────────────────────────────────────────────────────────
// Verdeling-input (gedeeld: API `.parse`/`.safeParse` + form `.safeParse`)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Eén per-regel keuze in een verdeler-actie: hoeveel van deze regel uit
 * voorraad geleverd wordt (rest = extern besteld). `regelId` verwijst naar de
 * bestaande regel (bv. `UitrustingBestellingRegel.id`); `aantalVoorraad` wordt
 * server-side geklemd op [0, aantal] én op de daadwerkelijk vrije voorraad
 * (nooit negatief saldo, terugval extern bij race).
 */
export const leverwijzeRegelKeuzeSchema = z.object({
  regelId: z.string().min(1),
  aantalVoorraad: z.number().int().min(0).max(99999),
})
export type LeverwijzeRegelKeuze = z.infer<typeof leverwijzeRegelKeuzeSchema>

/** Verdeling over een set regels (optioneel; ontbreekt -> server-default). */
export const leverwijzeVerdelingSchema = z.array(leverwijzeRegelKeuzeSchema).max(1000)
export type LeverwijzeVerdeling = z.infer<typeof leverwijzeVerdelingSchema>

// ─────────────────────────────────────────────────────────────────────────
// batch-62 uitrusting-inkoop (FO-467) — derde uitkomst "Al geleverd"
// ─────────────────────────────────────────────────────────────────────────

/**
 * De drie uitkomsten die de verwerker per regel ziet. `AL_GELEVERD` is geen
 * Prisma-leverwijze: de regel krijgt `VOORRAAD` (het kwam fysiek uit het
 * STREEP-magazijn) en de afwijking staat in `UitrustingRegelZonderBestelling`
 * met reden, actor en voorraadgevolg.
 */
export const LEVERWIJZE_UITKOMSTEN = ['VOORRAAD', 'EXTERN', 'AL_GELEVERD'] as const
export type LeverwijzeUitkomst = (typeof LEVERWIJZE_UITKOMSTEN)[number]

export const LEVERWIJZE_UITKOMST_LABELS: Record<LeverwijzeUitkomst, string> = {
  VOORRAAD: 'Uit voorraad',
  EXTERN: 'Extern bestellen',
  AL_GELEVERD: 'Al geleverd - verwerken zonder bestelling',
}

/**
 * De zichtbare, niet-onderdrukbare markering op een uitgifte die via de derde
 * leverwijze is verwerkt (spec 167 §7), naast de FO-381-markering.
 */
export const VERWERKT_ZONDER_VOORRAADMUTATIE_MARKERING = 'Verwerkt zonder voorraadmutatie'
